<template>
  <div
    v-click-outside="() => showSources = false"
    class="dropdown-container"
  >
    <div
      v-if="!isLoading"
      class="select-wrapper"
      :class="{ 'is-open': showSources }"
      @click="showSources = !showSources"
    >
      <div class="source-select">
        <span class="selected-text">{{ selectedSource ? selectedSource.name : 'Выберите источник' }}</span>
        <ChevronDown
          class="select-icon"
          :class="{ 'rotate-180': showSources }"
        />
      </div>
    </div>
    <div
      v-else
      class="loading-state"
    >
      Загрузка источников...
    </div>

    <div
      v-if="showSources && !isLoading"
      class="dropdown-list"
    >
      <div class="search-wrapper">
        <div class="search-input-container">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Поиск"
            class="search-input"
            @click.stop
          >
          <SearchIcon class="search-icon" />
        </div>
      </div>
      
      <div class="sources-list custom-scrollbar">
        <div 
          v-for="source in filteredSources" 
          :key="source.id" 
          class="source-item"
          :class="{ 'is-selected': selectedSource && selectedSource.id === source.id }"
          @click="selectSource(source)"
        >
          {{ source.name }}
        </div>
        <div
          v-if="filteredSources.length === 0"
          class="no-results"
        >
          Ничего не найдено
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, computed, defineEmits } from "vue";
import { useRoute } from "vue-router";
import { useSources } from "@/stores/sources-store";
import ChevronDown from "@/components/icons/ChevronDown.vue";
import SearchIcon from "@/components/icons/SearchIcon.vue";

const emit = defineEmits(["update:selectedSource"]);

const route = useRoute();
const sourcesStore = useSources();
const sources = ref([]);
const isLoading = ref(false);
const showSources = ref(false);
const searchQuery = ref("");
const selectedSource = ref(null);

const filteredSources = computed(() => {
  if (!searchQuery.value) return sources.value;
  const query = searchQuery.value.toLowerCase();
  return sources.value.filter(s => 
    s.name && s.name.toLowerCase().includes(query)
  );
});

const selectSource = (source) => {
  selectedSource.value = source;
  showSources.value = false;
  emit("update:selectedSource", source);
};

onMounted(async () => {
  isLoading.value = true;
  try {
    const data = await sourcesStore.getSources(null, null, 10000);
    sources.value = JSON.parse(JSON.stringify(data.results));
    
    const sourceId = route.params.sourceId || route.query.sourceId;
    if (sourceId) {
      const preselected = sources.value.find(s => String(s.id) === String(sourceId));
      if (preselected) {
        selectedSource.value = preselected;
      }
    }
  } catch (err) {
    console.error(err);
  } finally {
    isLoading.value = false;
  }
});
</script>

<style lang="scss" scoped>
.dropdown-container {
  position: relative;
  width: 240px; // Adjust width as needed
  font-family: 'Golos Text', sans-serif; // Assuming font from context or standard
}

.select-wrapper {
  background: #F8F9FA; // Light gray background like in screenshot
  border-radius: 8px;
  padding: 10px 16px;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;

  &:hover {
    background: #E9ECEF;
  }

  &.is-open {
    background: #E9ECEF;
  }
}

.source-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #6C757D; // Gray text color
  font-size: 14px;
  font-weight: 500;
}

.selected-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 8px;
}

.select-icon {
  width: 16px;
  height: 16px;
  color: #7B87A5;
  transition: transform 0.2s;

  &.rotate-180 {
    transform: rotate(180deg);
    color: #0D6EFD; // Blue color for chevron
  }
}

.dropdown-list {
  position: absolute;
  top: calc(100% + 8px);
  right: 0; // Align to right as in screenshot it seems to be on the right side of header
  width: 280px; // Wider than trigger
  background: #FFFFFF;
  border-radius: 12px;
  box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.12);
  z-index: 100;
  padding: 8px 0;
  overflow: hidden;
}

.search-wrapper {
  padding: 8px 12px;
}

.search-input-container {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  right: 12px;
  width: 14px;
  height: 14px;
  color: #ADB5BD;
  pointer-events: none;
}

.search-input {
  width: 100%;
  height: 36px;
  padding: 0 36px 0 12px;
  background: #F8F9FA;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  color: #212529;
  outline: none;

  &::placeholder {
    color: #ADB5BD;
  }

  &:focus {
    background: #FFFFFF;
    box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.25); // Focus ring
  }
}

.sources-list {
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  margin: 4px 12px;
}

.source-item {
  padding: 10px 16px;
  font-size: 14px;
  color: #212529;
  cursor: pointer;
  transition: background 0.2s;
  line-height: 1.4;

  &:hover {
    background: #F8F9FA;
  }

  &.is-selected {
    background: #E7F1FF;
    color: #0D6EFD;
  }
}

.no-results {
  padding: 12px 16px;
  font-size: 14px;
  color: #ADB5BD;
  text-align: center;
}

.loading-state {
  font-size: 14px;
  color: #ADB5BD;
}

// Custom scrollbar
.custom-scrollbar {
  &::-webkit-scrollbar {
    width: 12px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    margin-top: 4px;
    margin-bottom: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #D1E4FF;
    border-radius: 6px;
    border: 3px solid transparent;
    background-clip: content-box;
  }
}
</style>